import React from "react";
import { ColDef, SelectionChangedEvent } from "ag-grid-community";
import BaseGrid from "../../../components/BaseGrid";
import { useUserAssignmentGridStore } from "../store/UserAssignmentGridStore";
import { useUserAssignmentsContext } from "../context/UserAssignmentContext";
import { UserAssignment, UserAssignmentGridItem } from "../interfaces/UserAssignment";
import { useUsersContext } from "../../../contexts/UserContext";

export default function UserAssignmentGrid() {
    const { listOptions, isListOptionFetching, userAssignments } = useUserAssignmentsContext();
    const { users } = useUsersContext();
    const { setSelectedUserAssignment } = useUserAssignmentGridStore();

    const getOptionName = React.useCallback((optionId: number, categoryId: number): string => {
        const option = listOptions?.find(o => o.categoryId === categoryId)?.options.find(o => o.id === optionId);
        return option?.name ?? "N/A";
    }, [listOptions]);

    const getInfoUser = React.useCallback((userId: string, property: "displayName" | "userPrincipal" | "employeeId"): string => {
        const user = users?.find((user) => user.userPrincipal === userId);
        if (!user) return "N/A";
        const propertyMap = {
            displayName: user.displayName,
            userPrincipal: user.userPrincipal,
            employeeId: user.employeeId,
        };
        return propertyMap[property] ?? "N/A";
    }, [users]);

    const mapUserAssignmentToGridItem = React.useCallback((userAssignment: UserAssignment): UserAssignmentGridItem | undefined => {
        return {
            deviceId: userAssignment.deviceId,
            label: userAssignment.label,
            typeValue: getOptionName(userAssignment.type, 2),
            statusValue: getOptionName(userAssignment.status, 9),
            locationValue: getOptionName(userAssignment.location, 10),
            userId: userAssignment.userId,
            username: getInfoUser(userAssignment.userId, "displayName"),
            employeeId: getInfoUser(userAssignment.userId, "employeeId"),
            userPrincipal: getInfoUser(userAssignment.userId, "userPrincipal"),
        };
    }, [getOptionName, getInfoUser]);

    const gridData = React.useMemo(() =>
        userAssignments
            .map(mapUserAssignmentToGridItem)
            .filter((item): item is UserAssignmentGridItem => item !== undefined),
        [userAssignments, mapUserAssignmentToGridItem]
    );

    const colDefs = React.useMemo<ColDef[]>(() => [
        { field: "deviceId", headerName: "ID", filter: true, minWidth: 95, maxWidth: 105 },
        { field: "label", headerName: "Label", minWidth: 120 },
        { field: "typeValue", headerName: "Type", minWidth: 100 },
        { field: "statusValue", headerName: "Status", minWidth: 100 },
        { field: "locationValue", headerName: "Location", minWidth: 100 },
        { field: "employeeId", headerName: "Employee ID", minWidth: 150 },
        { field: "userPrincipal", headerName: "Mail", minWidth: 200 },
        { field: "username", headerName: "Username", minWidth: 150 },
    ], []);

    const onSelectionChanged = React.useCallback((e: SelectionChangedEvent) => {
        const selectedRow = e.api.getSelectedRows()[0] ?? null;
        setSelectedUserAssignment(selectedRow as UserAssignment);
    }, [setSelectedUserAssignment]);

    return (
        <BaseGrid<UserAssignmentGridItem>
            isSingle={true}
            isLoading={isListOptionFetching}
            data={gridData}
            colDefs={colDefs}
            onSelectionChanged={onSelectionChanged}
        />
    );
}
